import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { NgxCurrencyDirective } from 'ngx-currency';
import { Subject, takeUntil } from 'rxjs';

import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';
import { CsosnTexto, getCsosnTexto } from '@shared/constants/motor-fiscal/csosn-metadata';
import {
    IndicadoresCst,
    MapaCamposIndicador,
    aplicarObrigatoriedadeIndicadores,
    isAlgumCampoAtivo,
    isCampoAtivo,
    isCodigoDaOperacao,
    lerIndicadoresCst,
    limparCamposInativos,
} from '@shared/constants/motor-fiscal/indicadores-campos.util';
import { TipoOperacaoEnum } from '@shared/enums/motor-fiscal/tipo-operacao.enum';
import { AbntPipe } from '@shared/pipes/abnt.pipe';
import { MaskService } from '@shared/services/mask.service';
import { CsosnStruct } from '@shared/services/structs/motor-fiscal/csosn.struct';
import { ModalidadeBcOption } from '@shared/services/structs/motor-fiscal/modalidade-bc-option.struct';

/**
 * Campo do indicador do CSOSN -> control do grupo `icms` do item da venda. Os demais controls de uma chave são
 * valores apurados do mesmo campo, limpos junto.
 */
const MAPA_CAMPOS_CSOSN_VENDA: MapaCamposIndicador = {
    pCredSN: ['percentualCredito', 'valorCredito'],
    pRedBC: 'percentualReducaoBaseCalculo',
    pICMS: 'percentualAliquota',
    modBCST: 'codigoModalidadeBaseCalculoSt',
    pMVAST: 'percentualMvaSt',
    pRedBCST: 'percentualReducaoBaseCalculoSt',
    pICMSST: ['percentualAliquotaSt', 'valorBaseCalculoSt', 'valorIcmsSt'],
    pFCPST: ['percentualFcpSt', 'valorBaseFcpSt', 'valorFcpSt'],
    pST: 'percentualSuporteStRetido',
    pFCPSTRet: 'percentualFcpStRetido',
    pICMSEfet: 'percentualAliquotaEfetiva',
};

/**
 * Aba "Simples" (Simples Nacional e MEI, por CSOSN) do modal do item da venda (cópia da aba
 * Simples do item da NF-e de saída). Exclusiva com a aba ICMS: o modal decide pelo regime
 * tributário da natureza do documento. Grava no MESMO grupo `icms` do item (o banco garante CST ou CSOSN).
 */
@Component({
    selector: 'app-vd-item-aba-simples',
    standalone: true,
    imports: [
        AbntPipe,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatSelectModule,
        NgxCurrencyDirective,
        AlertCardComponent,
        EmptyStateComponent,
    ],
    templateUrl: './vd-item-aba-simples.component.html',
    styleUrls: ['../abas-comum.css', './vd-item-aba-simples.component.css'],
})
export class VdItemAbaSimplesComponent implements OnInit, OnChanges, OnDestroy {
    /** O grupo `icms` do item. */
    @Input({ required: true }) public form!: FormGroup;
    @Input() public listaCsosn: CsosnStruct[] = [];
    @Input() public listaModalidadeBcSt: ModalidadeBcOption[] = [];
    @Input() public isRecalculoPendente: boolean = false;

    public readonly percentMask = this.maskService.getPercentMask();
    public readonly valorMask = this.maskService.getCurrencyMask(2);

    private readonly destroy$ = new Subject<void>();

    constructor(private maskService: MaskService) {}

    public ngOnInit(): void {
        this.form.get('codigoCsosn')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.sincronizarObrigatoriedade());
        this.sincronizarObrigatoriedade();
    }

    /** Os indicadores chegam com a lista de CSOSN (lookup assíncrono). */
    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['listaCsosn'] && !changes['listaCsosn'].firstChange) this.sincronizarObrigatoriedade();
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public get indicadores(): IndicadoresCst {
        const csosn = this.form?.get('codigoCsosn')?.value;

        return lerIndicadoresCst(this.listaCsosn.find((c) => c.codigo === csosn)?.indicadores);
    }

    public get csosnTexto(): CsosnTexto | null {
        return getCsosnTexto(this.form?.get('codigoCsosn')?.value);
    }

    public get isComCsosn(): boolean {
        return !!this.form?.get('codigoCsosn')?.value;
    }

    /** Só os CSOSN de saída, pelo indicador. */
    public get listaCsosnDaOperacao(): CsosnStruct[] {
        return this.listaCsosn.filter((c) => isCodigoDaOperacao(c.indicadores, TipoOperacaoEnum.Saida));
    }

    public ativo(chave: string): boolean {
        return isCampoAtivo(this.indicadores, chave);
    }

    public algumAtivo(chaves: string[]): boolean {
        return isAlgumCampoAtivo(this.indicadores, chaves);
    }

    /** Há valor apurado no grupo que o CSOSN usa (crédito do Simples ou ICMS-ST). */
    public get isComValoresApurados(): boolean {
        return (this.ativo('pCredSN') && this.form.get('valorCredito')?.value != null)
            || (this.algumAtivo(['modBCST', 'pMVAST', 'pRedBCST', 'pICMSST', 'pFCPST']) && this.form.get('valorIcmsSt')?.value != null);
    }

    /** Troca feita pelo usuário: zera os campos que o novo CSOSN deixou de exibir. */
    public onCsosnSelecionado(): void {
        limparCamposInativos(this.form, this.indicadores, MAPA_CAMPOS_CSOSN_VENDA);
        this.sincronizarObrigatoriedade();
    }

    /** Mesma regra da natureza: obrigatório pelo indicador do CSOSN (inclusive o crédito, só onde o indicador exige). */
    private sincronizarObrigatoriedade(): void {
        const isComCsosn = !!this.form.get('codigoCsosn')?.value;

        aplicarObrigatoriedadeIndicadores(this.form, isComCsosn ? this.indicadores : null, MAPA_CAMPOS_CSOSN_VENDA);
    }
}
